'use client';

import React from 'react';
import Link from 'next/link';

import { RefreshCw, LogOut, ChevronLeft, ChevronRight, LayoutDashboard, ClipboardList, BarChart2 } from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    label: 'My Kit Inventory',
    href: '/my-kits',
    icon: <LayoutDashboard size={17} />,
    badge: 149,
  },
  {
    label: 'Replenishment',
    href: '/replenishment',
    icon: <RefreshCw size={17} />,
  },
  {
    label: 'My Orders',
    href: '/replenishment-orders',
    icon: <ClipboardList size={17} />,
  },
  {
    label: 'Admin Metrics',
    href: '/admin/replenishment-metrics',
    icon: <BarChart2 size={17} />,
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activePath: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({
  collapsed,
  onToggle,
  activePath,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col sidebar-transition relative z-30 ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
        style={{
          background: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          minHeight: '100vh',
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
        }}
      >
        <SidebarContent collapsed={collapsed} onToggle={onToggle} activePath={activePath} />
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col lg:hidden sidebar-transition ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          width: 'var(--sidebar-width)',
          background: '#ffffff',
          borderRight: '1px solid #e2e8f0',
        }}
      >
        <div className="absolute top-3 right-3">
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: '#64748b' }}
          >
            <ChevronLeft size={16} />
          </button>
        </div>
        <SidebarContent collapsed={false} onToggle={onToggle} activePath={activePath} />
      </aside>
    </>
  );
}

function SidebarContent({
  collapsed,
  onToggle,
  activePath,
}: {
  collapsed: boolean;
  onToggle: () => void;
  activePath: string;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo area */}
      <div
        className="flex items-center px-3 py-4"
        style={{ minHeight: '72px', borderBottom: '1px solid #e2e8f0' }}
      >
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0369a1, #0ea5e9)', boxShadow: '0 2px 8px rgba(3,105,161,0.25)' }}
          >
            <svg width="16" height="16" viewBox="0 0 48 40" fill="none">
              <path d="M4 28 C8 20, 16 12, 28 8 C20 14, 14 22, 12 32 Z" fill="white" opacity="0.9"/>
              <path d="M8 30 C14 18, 24 10, 36 6 C26 14, 18 24, 16 34 Z" fill="white" opacity="0.7"/>
              <path d="M2 24 C6 16, 14 8, 26 4 C18 12, 10 20, 8 30 Z" fill="white"/>
            </svg>
          </div>
          {!collapsed && (
            <div className="flex flex-col overflow-hidden">
              <span className="text-xs font-700 leading-tight truncate" style={{ color: '#0f2744' }}>
                HLL Lifecare
              </span>
              <span className="text-[10px] leading-tight truncate" style={{ color: '#64748b' }}>
                KitTrack System
              </span>
            </div>
          )}
        </div>
        <button
          onClick={onToggle}
          className="ml-auto p-1.5 rounded-md transition-colors flex-shrink-0"
          style={{ color: '#94a3b8' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#0369a1'; (e.currentTarget as HTMLButtonElement).style.background = '#f0f7ff'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-3 space-y-0.5 px-2">
        {!collapsed && (
          <p className="text-[10px] font-600 uppercase tracking-widest px-2 pb-2" style={{ color: '#94a3b8' }}>
            Navigation
          </p>
        )}
        {navItems.map((item) => {
          const isActive = activePath === item.href;
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-500 transition-all duration-150 ${isActive ? 'nav-active' : 'nav-inactive'}`}
              onMouseEnter={e => { if (!isActive) { (e.currentTarget as HTMLAnchorElement).style.background = '#f0f7ff'; (e.currentTarget as HTMLAnchorElement).style.color = '#0369a1'; } }}
              onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = '#64748b'; } }}
            >
              <span className="flex-shrink-0" style={{ color: isActive ? '#0369a1' : '#94a3b8' }}>{item.icon}</span>
              {!collapsed && (
                <>
                  <span className="truncate flex-1 text-xs leading-snug">{item.label}</span>
                  {item.badge !== undefined && (
                    <span
                      className="text-[10px] font-600 px-1.5 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        background: isActive ? 'rgba(3,105,161,0.12)' : 'rgba(220,38,38,0.1)',
                        color: isActive ? '#0369a1' : '#dc2626',
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User info + logout */}
      <div className="p-3 space-y-2" style={{ borderTop: '1px solid #e2e8f0' }}>
        {!collapsed ? (
          <>
            <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-700 text-white"
                style={{ background: 'linear-gradient(135deg, #0369a1, #0ea5e9)' }}
              >
                A
              </div>
              <div className="overflow-hidden flex-1">
                <p className="text-xs font-600 truncate" style={{ color: '#1a2332' }}>AIIMS PATNA</p>
                <p className="text-[10px] truncate" style={{ color: '#64748b' }}>Store Keeper</p>
              </div>
            </div>
            <Link
              href="/sign-up-login-screen"
              className="flex items-center gap-2 w-full px-2 py-2 rounded-lg text-xs transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#dc2626'; (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(220,38,38,0.06)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#64748b'; (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
            >
              <LogOut size={14} />
              <span>Logout</span>
            </Link>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 text-white"
              style={{ background: 'linear-gradient(135deg, #0369a1, #0ea5e9)' }}
              title="AIIMS PATNA"
            >
              A
            </div>
            <Link
              href="/sign-up-login-screen"
              className="p-1.5 rounded-md transition-colors"
              style={{ color: '#94a3b8' }}
              title="Logout"
            >
              <LogOut size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}